import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Users, Calendar, Trophy, Menu, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import MainNavigation from './navigation/MainNavigation';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabaseClient';

const Navbar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  // Handle click outside for hamburger menu
  const handleClickOutside = (event: MouseEvent) => {
    const menu = document.getElementById('hamburger-menu');
    if (menu && !menu.contains(event.target as Node)) {
      setMenuOpen(false);
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };
  // Hamburger menu toggle handler with event listener logic
  const toggleMenu = (event: React.MouseEvent) => {
    event.stopPropagation();

    const nextState = !menuOpen;
    setMenuOpen(nextState);

    if (nextState) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };
  const [logoUrl, setLogoUrl] = useState<string>('');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { user, userName, clubName, userRole, clubLogoPath, logout } = useAuth();

  // Fetch club logo path if user is a captain
  useEffect(() => {
    const fetchClubLogo = async () => {
      if (!user) return;

      if (userRole === 'admin') {
        setLogoUrl('https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/GGU.jpeg');
      } else if (userRole === 'captain' && user.club_id) {
        try {
          const { data, error } = await supabase
            .from('clubs')
            .select('logo_path')
            .eq('id', user.club_id)
            .single();

          if (error || !data?.logo_path) {
            throw error || new Error('No logo_path found');
          }

          setLogoUrl(`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${data.logo_path}`);
        } catch (err) {
          console.error('Error fetching club logo:', err);
          setLogoUrl('https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/GGU.jpeg');
        }
      } else {
        setLogoUrl('https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/GGU.jpeg');
      }
    };

    fetchClubLogo();
  }, [user, userRole]);

  const fullName = userName || user?.email || 'Unknown User';
  const clubDisplay = userRole === 'admin' ? 'Admin' : clubName || 'No Club';

  const navItems = [
    { name: 'Leagues', path: projectSlug ? `/projects/${projectSlug}/standings` : '#', icon: Trophy },
    { name: 'Fixtures', path: projectSlug ? `/projects/${projectSlug}/fixtures` : '#', icon: CalendarDays },
    { name: 'Teams', path: projectSlug ? `/projects/${projectSlug}/teams` : '#', icon: Users },
  ];

  const isActive = (path: string) => {
    if (path.endsWith('/standings')) {
      return (
        location.pathname === path ||
        location.pathname.startsWith(path) ||
        location.pathname === '/' ||
        (projectSlug && location.pathname === `/projects/${projectSlug}`)
      );
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="glass px-1 sm:px-6 py-2 flex items-center justify-between">
        {/* Left: Logo */}
        <div className="flex items-center space-x-2 pl-4">
          <button
            onClick={() => navigate(projectSlug ? `/projects/${projectSlug}/standings` : '/')}
            className="flex items-center space-x-2 focus:outline-none"
          >
            <div className="w-14 h-15 rounded-full overflow-hidden">
              <img
                src="/lovable-uploads/d4c7df19-9c74-4ee3-8b7d-3585fbf47e66.png"
                alt="Gwent Golf Union Logo"
                className="w-full h-full object-cover"
              />
            </div>
            {!isMobile && (
              <span className="text-xl font-medium">
                Gwent Golf Union
              </span>
            )}
          </button>
        </div>
        {/* Mobile: Custom Dropdown nav */}
        {isMobile && (
          <MobileNavDropdown
            navItems={navItems}
            currentPath={location.pathname}
            onNavigate={(path) => {
              navigate(path);
              setMenuOpen(false);
            }}
          />
        )}

        {/* Center: Dropdown nav */}
        {!isMobile && (
          <div className="flex gap-2">
            {navItems.map((item) => (
              <Button
                key={item.name}
                variant="ghost"
                size="sm"
                onClick={() => navigate(item.path)}
                className={cn(
                  'flex items-center gap-1',
                  isActive(item.path) ? 'text-[#30702F] font-semibold' : 'text-black'
                )}
              >
                <item.icon
                  className={cn(
                    'w-4 h-4',
                    isActive(item.path) ? 'text-[#30702F]' : 'text-black'
                  )}
                />
                {item.name}
              </Button>
            ))}
          </div>
        )}

        {/* Right: Hamburger */}
        <div className="flex items-center gap-1 relative pr-2">
          {/*
            Hamburger menu toggle with stopPropagation to avoid outside click handler
          */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            aria-expanded={menuOpen}
          >
            <Menu className="w-6 h-6" />
          </Button>
          {menuOpen && (
            <div id="hamburger-menu" className="absolute top-10 right-3 bg-white border rounded-md shadow-lg p-2 w-48 z-50">
              {user ? (
                <>
                  <div className="flex items-center gap-2 p-2">
                    <img
                      key={logoUrl}
                      src={logoUrl}
                      alt="Club Logo"
                      className="w-6 h-6 rounded-full object-cover"
                      onError={(e) => {
                        const fallback = 'https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/GGU.jpeg';
                        if (e.currentTarget.src !== fallback) {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = fallback;
                        }
                      }}
                    />
                    <div>
                      <p className="text-sm font-semibold">{fullName}</p>
                      <p className="text-xs text-gray-500">{clubDisplay}</p>
                    </div>
                  </div>
                  {userRole === 'admin' && (
                    <>
                      <div
                        className="mx-auto mt-2 text-center cursor-pointer text-[#30702F] hover:underline flex items-center justify-center gap-1 text-sm"
                        onClick={() => navigate(projectSlug ? `/projects/${projectSlug}/admin/usermanagement` : '/')}
                      >
                        <Users className="w-3.5 h-3.5" />
                        Users
                      </div>
                      <hr className="my-2 border-gray-300" />
                    </>
                  )}
                  <div
                    className="mx-auto mt-2 text-center cursor-pointer text-[#30702F] hover:underline flex items-center justify-center gap-1 text-sm"
                    onClick={() => window.open('/lovable-uploads/GGU-Mens Inter-Club Rules 2025.docx', '_blank')}
                  >
                    <img
                      src="/lovable-uploads/d4c7df19-9c74-4ee3-8b7d-3585fbf47e66.png"
                      alt="GGU Icon"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    View Rules
                  </div>
                  <div
                    className="mx-auto mt-2 text-center cursor-pointer text-[#30702F] hover:underline flex items-center justify-center gap-1 text-sm"
                    onClick={() => window.location.href = 'mailto:joeash5@gmail.com'}
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.94 5.18a2 2 0 012.02-1.7h10.08a2 2 0 012.02 1.7L10 10.63 2.94 5.18zM2 6.8v7.4a2 2 0 002 2h12a2 2 0 002-2V6.8l-7.4 5.48a1 1 0 01-1.2 0L2 6.8z" />
                    </svg>
                    Contact
                  </div>
                  <hr className="my-2 border-gray-300" />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full bg-[#30702F] text-white hover:bg-[#245824]"
                    onClick={handleLogout}
                  >
                    Sign out
                  </Button>
                </>
              ) : (
                <>
                  <div
                    className="mx-auto mt-2 text-center cursor-pointer text-[#30702F] hover:underline flex items-center justify-center gap-1 text-sm"
                    onClick={() => window.open('/lovable-uploads/GGU-Mens Inter-Club Rules 2025.docx', '_blank')}
                  >
                    <img
                      src="/lovable-uploads/d4c7df19-9c74-4ee3-8b7d-3585fbf47e66.png"
                      alt="GGU Icon"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    View Rules
                  </div>
                  <div
                    className="mx-auto mt-2 text-center cursor-pointer text-[#30702F] hover:underline flex items-center justify-center gap-1 text-sm"
                    onClick={() => window.location.href = 'mailto:gguleagues@outlook.com'}
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.94 5.18a2 2 0 012.02-1.7h10.08a2 2 0 012.02 1.7L10 10.63 2.94 5.18zM2 6.8v7.4a2 2 0 002 2h12a2 2 0 002-2V6.8l-7.4 5.48a1 1 0 01-1.2 0L2 6.8z" />
                    </svg>
                    Contact
                  </div>
                  <hr className="my-2 border-gray-300" />
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full bg-[#30702F] text-white hover:bg-[#245824]"
                    onClick={() => navigate('/login')}
                  >
                    Sign in
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
// Custom mobile nav dropdown component
const MobileNavDropdown: React.FC<{
  navItems: { name: string; path: string; icon: React.ElementType }[];
  currentPath: string;
  onNavigate: (path: string) => void;
}> = ({ navItems, currentPath, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const current = navItems.find((item) => currentPath.startsWith(item.path)) || navItems[0];
  // Close dropdown on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const dropdown = document.getElementById('mobile-nav-dropdown');
      if (dropdown && !dropdown.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  return (
    <div className="relative ml-2" id="mobile-nav-dropdown">
      <Button
        variant="outline"
        size="sm"
        className={cn(
          'flex items-center gap-1 min-w-[100px] justify-between',
          'text-[#30702F] border-[#30702F]'
        )}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <current.icon className="w-4 h-4 text-[#30702F]" />
        {current.name}
        <svg className={cn('w-4 h-4 ml-1', open ? 'text-[#30702F]' : 'text-black')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </Button>
      {open && (
        <div
          className="absolute left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-full"
          style={{ minWidth: '140px' }}
        >
          {navItems.map((item) => (
            <button
              key={item.name}
              className={cn(
                'flex items-center w-full px-3 py-2 text-sm hover:bg-gray-100 gap-2',
                item.path === currentPath ? 'text-[#30702F] font-semibold' : 'text-black'
              )}
              onClick={() => {
                setOpen(false);
                onNavigate(item.path);
              }}
              tabIndex={0}
              type="button"
            >
              <item.icon className={cn('w-4 h-4', item.path === currentPath ? 'text-[#30702F]' : 'text-black')} />
              {item.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};